// src/modules/gyms/services/gym.service.ts

import {
  GymBrand,
  GymBranch,
  GymService,
  GymServiceCategory,
  GymServiceImage,
  GymServiceUnitType,
  AMENITIES_LIST,
  BranchImage,
  CategoryImage,
} from "../models/gym.model";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import {
  API_HEADER,
  API_URL,
  BASE_URL,
} from "@modules/shared/enums/common-enum";
import {
  buildPagingQuery,
  extractList,
  fetchAllPaginated,
  shouldContinuePaging,
} from "@modules/shared/services/pagination.service";
import { AuthArgs } from "../types/auth.types";

/** Mapper: B2B API → GymBrand */
const mapB2BToBrand = (item: any): GymBrand => ({
  id: item.id,
  contractId: item.contract_id || "",
  name: item.name || "",
  contactEmail: item.contact_email || "",
  contactPhone: item.contact_phone || "",
  logo: item.logo?.url || null,
  banner: item.banner?.url || null,
  description: item.description || "",
  scan_record: item.scan_record || null,
  showSocial: item.show_social ?? false,
  createdAt: item.created_at || item.createdAt || new Date().toISOString(),
  updatedAt: item.updated_at || item.updatedAt || new Date().toISOString(),
});

/** Mapper: API Branch → GymBranch (chỉ dùng images, không gallery) */
const mapBranchToGymBranch = (item: any): GymBranch => {
  const openingHours = item.opening_hours || {};
  const monday = openingHours.monday || {
    start_time: "06:00",
    end_time: "22:00",
  };

  const amenities = [
    ...(item.amenities || []),
    ...(item.other_amenities || []),
  ];

  const images: BranchImage[] = (item.images || [])
    .filter((img: any) => img?.media?.url)
    .map((img: any) => ({
      url: img.media.url,
      key: img.media.key || img.media.url,
      category: img.category,
    }));

  const categories = Array.isArray(item.categories) ? item.categories : [];

  let address = "";
  let lat: number | null = null;
  let lng: number | null = null;
  if (item.address) {
    if (typeof item.address === "string") {
      address = item.address;
    } else {
      address =
        item.address.raw_address ||
        item.address.formatted ||
        item.address.text ||
        "";
      const coord = item.address.coordinate ?? item.address;
      lat = coord?.lat ?? item.address.lat ?? null;
      lng = coord?.long ?? item.address.long ?? null;
    }
  }

  return {
    id: item.id,
    brandId: item.b2b_id || "",
    name: item.name || "",
    address: address,
    lat,
    lng,
    operatingHoursFrom: monday.start_time || "06:00",
    operatingHoursTo: monday.end_time || "22:00",
    amenities,
    description: item.description || "",
    ctaLink: item.cta_link || "",
    images,
    openingHours,
    isActive: item.is_active ?? true,
    categories,
    createdAt: item.created_at || item.createdAt || new Date().toISOString(),
    updatedAt: item.updated_at || item.updatedAt || new Date().toISOString(),
  };
};

/** Build payload for branch create/update */
const mapGymBranchToApi = (
  branch: Omit<GymBranch, "id" | "createdAt" | "updatedAt">,
  isUpdate: boolean = false,
  openingHoursOverride?: Record<
    string,
    { start_time: string; end_time: string }
  >,
) => {
  if (!isUpdate && !branch.brandId) {
    throw new Error("brandId is required to create branch");
  }

  const validAmenities = [
    "motorcycle_parking",
    "car_parking",
    "locker",
    "hot_cold_shower",
    "sauna",
    "stretching_room",
    "inbody",
    "free_water",
    "free_towel",
    "open_24_7",
    "allow_freelancer",
  ];

  const amenities: string[] = [];
  const otherAmenities: string[] = [];

  branch.amenities.forEach((a) => {
    if (validAmenities.indexOf(a)) {
      amenities.push(a);
    } else {
      otherAmenities.push(a);
    }
  });

  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  let openingHours: any;
  if (openingHoursOverride) {
    openingHours = days.reduce((acc, day) => {
      acc[day] = {
        start_time: openingHoursOverride[day]?.start_time || "06:00",
        end_time: openingHoursOverride[day]?.end_time || "22:00",
      };
      return acc;
    }, {} as any);
  } else {
    openingHours = days.reduce((acc, day) => {
      acc[day] = {
        start_time: branch.operatingHoursFrom || "06:00",
        end_time: branch.operatingHoursTo || "22:00",
      };
      return acc;
    }, {} as any);
  }

  const validImageCategories = Object.keys(CategoryImage).map(
    (key) => CategoryImage[key as keyof typeof CategoryImage],
  );
  const sourceImages = branch.images || [];
  const imagesPayload = sourceImages.map((img) => {
    const category =
      img.category &&
      validImageCategories.indexOf(img.category as CategoryImage)
        ? img.category
        : CategoryImage.GYM;
    return {
      media: { url: img.url, key: img.key },
      category,
    };
  });

  const addressPayload: Record<string, unknown> = {
    raw_address: branch.address.trim(),
  };
  if (branch.lat != null && branch.lng != null) {
    addressPayload.coordinate = { lat: branch.lat, long: branch.lng };
  }

  const payload: any = {
    name: branch.name,
    address: addressPayload,
    images: imagesPayload,
    amenities,
    other_amenities: otherAmenities,
    opening_hours: openingHours,
    cta_link: branch.ctaLink || "",
    description: branch.description || "",
    is_active: branch.isActive !== undefined ? branch.isActive : true,
    categories: branch.categories || [],
  };
  if (!isUpdate && otherAmenities.length > 0) {
    payload.other_amenities = otherAmenities;
  }

  if (!isUpdate) {
    payload.b2b_id = branch.brandId;
  }

  return payload;
};

/** Mapper: service images from API */
const mapServiceImages = (raw: any): GymServiceImage[] => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw
    .map((img: any) => {
      const url = img?.url || img?.media?.url;
      const key = img?.key || img?.media?.key || url;
      if (!url) return null;
      return { url, key };
    })
    .filter(Boolean) as GymServiceImage[];
};

/** Mapper: API Service → GymService */
const mapServiceToGymService = (item: any, brandId: string): GymService => {
  const images = mapServiceImages(item.image);
  const priceRaw = item.price?.$numberDecimal ?? item.price;
  return {
    id: item.id,
    brandId,
    branchId: item.branch_id || item.branchId || "",
    name: item.name || "",
    category: (item.category || "GYM") as GymServiceCategory,
    price: parseInt(String(priceRaw), 10) || 0,
    amount: Number(item.amount) || 1,
    unitType: (item.unit_type ||
      item.unitType ||
      "SESSION") as GymServiceUnitType,
    description: item.description || "",
    active: item.active ?? true,
    images,
    expirationAt: item.expiration_at || item.expirationAt || null,
    createdAt: item.created_at || item.createdAt || new Date().toISOString(),
    updatedAt: item.updated_at || item.updatedAt || new Date().toISOString(),
  };
};

type ServiceWriteInput = Omit<GymService, "id" | "createdAt" | "updatedAt">;

/** Build create payload. SESSION requires expiration_at; DAY/MONTH leave it to BE. */
const mapGymServiceToCreateApi = (service: ServiceWriteInput) => {
  const payload: Record<string, unknown> = {
    name: service.name.trim(),
    branch_id: service.branchId,
    category: service.category,
    price: String(service.price),
    amount: service.amount,
    unit_type: service.unitType,
    description: service.description?.trim() || undefined,
    active: service.active,
    image: service.images.length > 0 ? service.images : null,
  };
  if (service.unitType === "SESSION") {
    if (!service.expirationAt) {
      throw new Error("Ngày hết hạn bắt buộc với loại Buổi (SESSION)");
    }
    payload.expiration_at = service.expirationAt;
  }
  return payload;
};

/** Build update payload. Omit unit_type/amount/expiration_at unless they changed */
const mapGymServiceToUpdateApi = (
  service: ServiceWriteInput,
  previous?: GymService | null,
) => {
  const payload: Record<string, unknown> = {
    name: service.name.trim(),
    category: service.category,
    price: String(service.price),
    description: service.description?.trim() || undefined,
    active: service.active,
    image: service.images.length > 0 ? service.images : null,
  };

  const unitChanged =
    !previous ||
    previous.unitType !== service.unitType ||
    previous.amount !== service.amount;
  const sessionExpChanged =
    service.unitType === "SESSION" &&
    (!previous || previous.expirationAt !== service.expirationAt);

  if (unitChanged || sessionExpChanged) {
    payload.amount = service.amount;
    payload.unit_type = service.unitType;
    if (service.unitType === "SESSION") {
      if (!service.expirationAt) {
        throw new Error("Ngày hết hạn bắt buộc với loại Buổi (SESSION)");
      }
      payload.expiration_at = service.expirationAt;
    }
  }
  return payload;
};

/** Helper: headers for multipart/form-data (remove Content-Type) */
const getFormHeaders = (): HeadersInit => {
  const { "Content-Type": _, ...rest } = API_HEADER as Record<string, string>;
  return rest;
};

/** Service class */
class GymPartnerService {
  // ---------- Brands ----------
  async getAllBrands(authArgs: AuthArgs): Promise<GymBrand[]> {
    try {
      const response = await fetchWithAuth(
        `${BASE_URL}/b2b?business_model=gym`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      let data = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(data)) data = [];
      return data.map(mapB2BToBrand);
    } catch (error) {
      console.error("getAllBrands error:", error);
      return [];
    }
  }

  async getBrandById(
    id: string,
    authArgs: AuthArgs,
  ): Promise<GymBrand | undefined> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B}/${id}`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      return mapB2BToBrand(response?.data ?? response);
    } catch {
      return undefined;
    }
  }

  async createBrand(
    brand: Omit<GymBrand, "id" | "createdAt" | "updatedAt">,
    authArgs: AuthArgs,
  ): Promise<GymBrand> {
    const payload = {
      contract_id: brand.contractId,
      name: brand.name,
      members: [],
      description: brand.description,
      show_social: brand.showSocial,
      contact_email: brand.contactEmail,
      contact_phone: brand.contactPhone,
      scan_record: brand.scan_record,
    };
    const response = await fetchWithAuth(
      API_URL.B2B,
      {
        method: "POST",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapB2BToBrand(response);
  }

  async updateBrand(
    id: string,
    updates: Partial<GymBrand>,
    authArgs: AuthArgs,
  ): Promise<GymBrand | undefined> {
    const current = await this.getBrandById(id, authArgs);
    if (!current) return undefined;
    const merged = { ...current, ...updates };
    const payload = {
      name: merged.name,
      description: merged.description,
      show_social: merged.showSocial,
      scan_record: merged.scan_record,
    };
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${id}`,
      {
        method: "PUT",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapB2BToBrand(response);
  }

  async deleteBrand(id: string, authArgs: AuthArgs): Promise<boolean> {
    await fetchWithAuth(
      `${API_URL.B2B}/${id}`,
      { method: "DELETE", headers: API_HEADER },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return true;
  }

  // ---------- Branches ----------
  async getAllBranches(authArgs: AuthArgs): Promise<GymBranch[]> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B_BRANCH}`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      let data = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(data)) data = [];
      return data.map(mapBranchToGymBranch);
    } catch (error) {
      console.error("getAllBranches error:", error);
      return [];
    }
  }

  async getBranchesByBrand(
    brandId: string,
    authArgs: AuthArgs,
  ): Promise<GymBranch[]> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B_BRANCH}/b2b/${brandId}`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      let data = Array.isArray(response) ? response : response?.data;
      if (!Array.isArray(data)) data = [];
      return data.map(mapBranchToGymBranch);
    } catch (error) {
      console.error("getBranchesByBrand error:", error);
      return [];
    }
  }

  async createBranch(
    branch: Omit<GymBranch, "id" | "createdAt" | "updatedAt">,
    authArgs: AuthArgs,
    openingHoursOverride?: Record<
      string,
      { start_time: string; end_time: string }
    >,
  ): Promise<GymBranch> {
    const payload = mapGymBranchToApi(branch, false, openingHoursOverride);
    const response = await fetchWithAuth(
      `${API_URL.B2B_BRANCH}`,
      {
        method: "POST",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapBranchToGymBranch(response);
  }

  async updateBranch(
    id: string,
    updates: Partial<GymBranch>,
    authArgs: AuthArgs,
    openingHoursOverride?: Record<
      string,
      { start_time: string; end_time: string }
    >,
  ): Promise<GymBranch | undefined> {
    const getResponse = await fetchWithAuth(
      `${API_URL.B2B_BRANCH}/${id}`,
      { method: "GET", headers: API_HEADER },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    const current = mapBranchToGymBranch(getResponse);
    const merged = { ...current, ...updates };
    const payload = mapGymBranchToApi(merged, true, openingHoursOverride);
    const updatedResponse = await fetchWithAuth(
      `${API_URL.B2B_BRANCH}/${id}`,
      {
        method: "PUT",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapBranchToGymBranch(updatedResponse);
  }

  async deleteBranch(id: string, authArgs: AuthArgs): Promise<boolean> {
    await fetchWithAuth(
      `${API_URL.B2B_BRANCH}/${id}`,
      { method: "DELETE", headers: API_HEADER },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return true;
  }

  // ---------- Services ----------
  async getAllServices(authArgs: AuthArgs): Promise<GymService[]> {
    try {
      const branches = await this.getAllBranches(authArgs);
      const nested = await Promise.all(
        branches.map((branch) =>
          this.getServicesByBranch(branch.id, branch.brandId, authArgs),
        ),
      );
      return nested.reduce((acc, val) => acc.concat(val), [] as GymService[]);
    } catch (error) {
      console.error("getAllServices error:", error);
      return [];
    }
  }

  async getServicesByBranch(
    branchId: string,
    brandId: string,
    authArgs: AuthArgs,
    soft = true,
  ): Promise<GymService[]> {
    try {
      return await fetchAllPaginated<GymService>(async (page, limit) => {
        const response = await fetchWithAuth(
          `${API_URL.B2B_BRANCH_SERVICES(branchId)}?${buildPagingQuery(page, limit, -1)}`,
          { method: "GET", headers: API_HEADER },
          authArgs.auth,
          authArgs.setAuth,
          authArgs.logout,
        );
        const items = extractList(response).map((item: any) =>
          mapServiceToGymService(item, brandId),
        );
        const hasMore = shouldContinuePaging(items, page, limit, response);
        return { items, hasMore };
      });
    } catch (error) {
      console.error("getServicesByBranch error:", error);
      if (soft) return [];
      throw error;
    }
  }

  async getServicesByBrand(
    brandId: string,
    authArgs: AuthArgs,
    knownBranches?: Pick<GymBranch, "id" | "brandId">[],
  ): Promise<GymService[]> {
    try {
      const branches =
        knownBranches && knownBranches.length > 0
          ? knownBranches
          : await this.getBranchesByBrand(brandId, authArgs);

      const results: GymService[] = [];
      for (const branch of branches) {
        try {
          const services = await this.getServicesByBranch(
            branch.id,
            brandId,
            authArgs,
            false,
          );
          results.push(...services);
        } catch {
          break;
        }
      }
      return results;
    } catch (error) {
      console.error("getServicesByBrand error:", error);
      return [];
    }
  }

  async createService(
    service: ServiceWriteInput,
    authArgs: AuthArgs,
  ): Promise<GymService> {
    if (!service.branchId) {
      throw new Error("Service must belong to a branch (branchId required)");
    }
    const payload = mapGymServiceToCreateApi(service);
    const response = await fetchWithAuth(
      API_URL.B2B_BRANCH_SERVICE,
      {
        method: "POST",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapServiceToGymService(response?.data ?? response, service.brandId);
  }

  async updateService(
    id: string,
    updates: Partial<GymService> & { branchId: string },
    authArgs: AuthArgs,
  ): Promise<GymService | undefined> {
    const branchId = updates.branchId;
    if (!branchId) {
      throw new Error(`Service ${id} does not have branchId`);
    }

    const current = await this.getServiceDetail(
      branchId,
      id,
      updates.brandId || "",
      authArgs,
    );
    if (!current) {
      throw new Error(`Service ${id} not found`);
    }

    const brandId = updates.brandId || current.brandId;
    const merged: ServiceWriteInput = {
      brandId,
      branchId,
      name: updates.name ?? current.name,
      category: updates.category ?? current.category,
      price: updates.price ?? current.price,
      amount: updates.amount ?? current.amount,
      unitType: updates.unitType ?? current.unitType,
      description: updates.description ?? current.description,
      active: updates.active ?? current.active,
      images: updates.images ?? current.images,
      expirationAt:
        updates.expirationAt !== undefined
          ? updates.expirationAt
          : current.expirationAt,
    };

    const explicitKeys = Object.keys(updates).filter(
      (k) => k !== "branchId" && k !== "brandId" && k !== "id",
    );
    const isPartialToggle =
      explicitKeys.length === 1 && explicitKeys[0] === "active";

    const payload = isPartialToggle
      ? { active: merged.active }
      : mapGymServiceToUpdateApi(merged, current);

    const response = await fetchWithAuth(
      `${API_URL.B2B_BRANCH_SERVICES(branchId)}/${id}`,
      {
        method: "PUT",
        headers: API_HEADER,
        body: JSON.stringify(payload),
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return mapServiceToGymService(response?.data ?? response, brandId);
  }

  async deleteService(
    id: string,
    branchId: string,
    authArgs: AuthArgs,
  ): Promise<boolean> {
    await fetchWithAuth(
      `${API_URL.B2B_BRANCH_SERVICES(branchId)}/${id}`,
      { method: "DELETE", headers: API_HEADER },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );
    return true;
  }

  // ---------- Media Upload ----------
  async uploadMedia(
    file: File,
    authArgs: AuthArgs,
  ): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetchWithAuth(
      `${API_URL.MEDIA_URL}/upload`,
      {
        method: "POST",
        headers: getFormHeaders(),
        body: formData,
      },
      authArgs.auth,
      authArgs.setAuth,
      authArgs.logout,
    );

    const data = response?.data || response;
    return { url: data.url, key: data.key };
  }

  async getServiceDetail(
    branchId: string,
    serviceId: string,
    brandId: string,
    authArgs: AuthArgs,
  ): Promise<GymService | undefined> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B_BRANCH_SERVICES(branchId)}/${serviceId}`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      return mapServiceToGymService(response?.data ?? response, brandId);
    } catch {
      return undefined;
    }
  }

  // ---------- Utilities ----------
  getAmenities() {
    return AMENITIES_LIST;
  }

  async getBranchCount(authArgs: AuthArgs): Promise<number> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B_BRANCH}?limit=1&page=1`,
        { method: "GET", headers: API_HEADER },
        authArgs.auth,
        authArgs.setAuth,
        authArgs.logout,
      );
      const total = response?.pagination?.total ?? 0;
      return total;
    } catch (error) {
      console.error("getBranchCount error:", error);
      const branches = await this.getAllBranches(authArgs);
      return branches.length;
    }
  }
}

export const gymPartnerService = new GymPartnerService();
